interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<any>>();
const inFlight = new Map<string, Promise<any>>();

const DEFAULT_TTL_MS = 60_000;

/**
 * Wraps an async function with in-memory cache + request deduplication.
 * - Returns cached data if within TTL
 * - Deduplicates concurrent calls with the same key
 */
export async function dedupedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS
): Promise<T> {
  const existing = cache.get(key);
  if (existing && Date.now() - existing.timestamp < ttlMs) {
    return existing.data;
  }

  if (inFlight.has(key)) {
    return inFlight.get(key)!;
  }

  const promise = fetcher()
    .then((data) => {
      cache.set(key, { data, timestamp: Date.now() });
      inFlight.delete(key);
      return data;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });

  inFlight.set(key, promise);
  return promise;
}

export function invalidateCache(key: string): void {
  cache.delete(key);
  inFlight.delete(key);
}

export function clearAllCache(): void {
  cache.clear();
}
